import React, { useState, useEffect, useMemo } from 'react';
import Label from 'components/_utility/Label';
import { getImageBaseUrl } from 'helpers/request';
import { useSelector } from 'react-redux';
import { getLoadedConfiguration, getProvincialData } from 'src/redux/app/selectors';
import usePrice from 'src/hooks/usePrice';
import InfoCircle from 'src/assets/icons/info-circle.svg';
import Modal from 'src/components/Modal/Modal';
import {
  Wrap,
  BikeOverview,
  BikeImageWrap,
  BikeDetailsWrap,
  BikeDescription,
  Tagline,
  BikeName,
  BikePriceWrap,
  BikePrice,
  InfoCircleWrap,
  BikeImage,
  BikeConfiguration,
  ItemPriceRow,
  Divider,
} from './BikeCard.styled';

const BikeCard = ({ bikeModel }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [priceDisclaimer, setPriceDisclaimer] = useState('');
  const loadedConfiguration = useSelector(getLoadedConfiguration);
  const formatPrice = usePrice();
  const provincialData = useSelector(getProvincialData);
  const servicingPacksTotal = loadedConfiguration?.extras?.servicingPacks?.reduce(
    (prevTotal, servicingPack) => prevTotal + Number(servicingPack.price || 0),
    0
  ) || 0;
  const warrantyPacksTotal = Number(loadedConfiguration?.extras?.warrantyPacks?.[0]?.price || 0);
  const extrasTotal = servicingPacksTotal + warrantyPacksTotal;

  const decodeHTML = html => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  };

  const hasProvincialFees =
    provincialData?.fees?.dealerFee !== undefined || provincialData?.fees?.freightFee !== undefined;

  const provincialFeesTotal = hasProvincialFees
    ? Number(provincialData.fees.dealerFee || 0) + Number(provincialData.fees.freightFee || 0)
    : 0;

  const feesTotal = useMemo(() => {
    return loadedConfiguration?.extras?.fees?.reduce((prevTotal, fee) => prevTotal + Number(fee?.price || 0), 0) || 0;
  }, [loadedConfiguration?.extras?.fees]);

  const freightFee = useMemo(() => {
    return loadedConfiguration?.extras?.fees?.filter(fee => fee.title.toLowerCase().includes('freight'))[0];
  }, [loadedConfiguration?.extras?.fees]);

  const dealerFee = useMemo(() => {
    return loadedConfiguration?.extras?.fees?.filter(fee => fee.title.toLowerCase().includes('dealer'))[0];
  }, [loadedConfiguration?.extras?.fees]);

  const dealerAndFreightFees = useMemo(() => {
    return loadedConfiguration?.extras?.fees?.filter(
      fee => fee.title.toLowerCase().includes('dealer') || fee.title.toLowerCase().includes('freight')
    );
  }, [loadedConfiguration?.extras?.fees]);
  const configurationCost = loadedConfiguration && bikeModel ? (loadedConfiguration.price - bikeModel.price - extrasTotal - feesTotal) : 0;

  useEffect(() => {
    const priceDisclaimerScript = document.getElementById('price-disclaimer-wrapper');

    const priceDisclaimerText = priceDisclaimerScript ? priceDisclaimerScript.textContent?.trim() ?? '' : '';
    if (priceDisclaimerText) {
      setPriceDisclaimer(decodeHTML(priceDisclaimerText));
    }
  }, []);

  return (
    <Wrap>
      <BikeOverview full={!loadedConfiguration && !bikeModel?.description}>
        <BikeDetailsWrap>
          <Tagline>
            <Label name="yourBikeLabel"></Label>
          </Tagline>
          <BikeName dangerouslySetInnerHTML={{ __html: bikeModel?.name || '' }}></BikeName>
          <BikePriceWrap>
            <BikePrice>
              {formatPrice(
                hasProvincialFees
                  ? (bikeModel?.price || 0) + Math.max(0, configurationCost) + extrasTotal + provincialFeesTotal
                  : loadedConfiguration?.price || bikeModel?.price || 0
              )}
            </BikePrice>
            {priceDisclaimer && (
              <InfoCircleWrap
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                <InfoCircle />
              </InfoCircleWrap>
            )}

            <Modal content={`${priceDisclaimer}`} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
          </BikePriceWrap>
        </BikeDetailsWrap>
        {bikeModel?.thumbnail && (
          <BikeImageWrap>
            <BikeImage
              src={`${getImageBaseUrl()}${loadedConfiguration?.image?.src || bikeModel?.thumbnail?.src}`}
              alt={loadedConfiguration?.image?.alt || bikeModel?.thumbnail?.alt}
            ></BikeImage>
          </BikeImageWrap>
        )}
      </BikeOverview>

      {loadedConfiguration && (
        <BikeConfiguration>
          <Tagline>
            <Label name="summaryLabel"></Label>
          </Tagline>
          {provincialData?.province && (
            <ItemPriceRow>
              <span>
                <Label name="provinceLabel"></Label>
              </span>
              <span>{provincialData.province}</span>
            </ItemPriceRow>
          )}
          <ItemPriceRow>
            <span dangerouslySetInnerHTML={{ __html: bikeModel?.name || '' }}></span>
            <span>{formatPrice(bikeModel?.price || 0)}</span>
          </ItemPriceRow>
          <ItemPriceRow>
            <span>
              <Label name="configurationsLabel"></Label>
            </span>
            <span>{formatPrice(Math.max(0, configurationCost))}</span>
          </ItemPriceRow>
          {loadedConfiguration.extras.warrantyPacks.length ? (
            <ItemPriceRow>
              <span>{loadedConfiguration.extras.warrantyPacks[0].title}</span>
              <span>{formatPrice(warrantyPacksTotal)}</span>
            </ItemPriceRow>
          ) : null}
          {loadedConfiguration.extras.servicingPacks.length ? (
            <ItemPriceRow>
              <span>{loadedConfiguration.extras.servicingPacks[0].title}</span>
              <span>
                {loadedConfiguration.extras.servicingPacks[0].priceLocalised ? (
                  formatPrice(servicingPacksTotal)
                ) : (
                  <Label name="quoteRequestLabel"></Label>
                )}
              </span>
            </ItemPriceRow>
          ) : null}
          {hasProvincialFees && (
            <>
              {provincialData.fees.freightFee !== undefined && freightFee && (
                <ItemPriceRow>
                  <span>{freightFee.title}</span>
                  <span>{formatPrice(provincialData.fees.freightFee)}</span>
                </ItemPriceRow>
              )}
              {provincialData.fees.dealerFee !== undefined && dealerFee && (
                <ItemPriceRow>
                  <span>{dealerFee.title}</span>
                  <span>{formatPrice(provincialData.fees.dealerFee)}</span>
                </ItemPriceRow>
              )}
            </>
          )}
          {!hasProvincialFees &&
            dealerAndFreightFees?.map(fee => (
              <ItemPriceRow key={fee.id}>
                <span>{fee.title}</span>
                <span>{fee.priceLocalised || formatPrice(fee.price)}</span>
              </ItemPriceRow>
            ))}
          <Divider></Divider>
          <ItemPriceRow bold>
            <span>
              <Label name="totalLabel"></Label>
            </span>
            <span>
              {formatPrice(
                hasProvincialFees
                  ? (bikeModel?.price || 0) + Math.max(0, configurationCost) + extrasTotal + provincialFeesTotal
                  : loadedConfiguration?.price || bikeModel?.price || 0
              )}
            </span>
          </ItemPriceRow>
        </BikeConfiguration>
      )}
      {!loadedConfiguration && bikeModel?.description && (
        <BikeDescription dangerouslySetInnerHTML={{ __html: bikeModel.description }}></BikeDescription>
      )}
    </Wrap>
  );
};

export default BikeCard;
